import { ErrorOutlined as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';

import { caloTheme } from '../../assets/themes/calo';

interface ErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      globalThis.location.reload();
    }
  };

  const errorDetails = error instanceof Error ? error : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: 4,
        textAlign: 'center'
      }}
    >
      <Stack
        spacing={3}
        sx={{
          alignItems: 'center',
          maxWidth: 500
        }}
      >
        <Box
          sx={{
            backgroundColor: caloTheme.palette.secondaryRed50,
            borderRadius: '50%',
            padding: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: caloTheme.palette.red }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, color: caloTheme.palette.neutral900 }}>
          Something went wrong
        </Typography>

        <Typography variant="body1" sx={{ color: caloTheme.palette.neutral600 }}>
          We apologize for the inconvenience. The error has been automatically reported to our team.
        </Typography>

        {process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' && errorDetails && (
          <Box
            sx={{
              backgroundColor: caloTheme.palette.neutral100,
              borderRadius: 2,
              padding: 2,
              width: '100%',
              textAlign: 'left',
              overflow: 'auto',
              maxHeight: 150
            }}
          >
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: caloTheme.palette.red }}>
              {errorDetails.name}: {errorDetails.message}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              backgroundColor: caloTheme.palette.primary500,
              '&:hover': {
                backgroundColor: caloTheme.palette.primary600
              }
            }}
          >
            Try Again
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ErrorFallback;
